import { Modal, SubmitButton } from "@xola/ui-kit";
import { FormField, isExperienceProduct, SecondaryButton } from "common";
import { Formik } from "formik";
import { uniqBy } from "lodash";
import React, { useMemo } from "react";
import { ExperienceStatus, Merchandise, MerchandiseSource, Product, ProductObject } from "types";
import { DragDropComponent } from "../ButtonCode/MultipleItemCheckoutModal/DragAndDropComponent";
import { SearchInput } from "@/components/Common/SearchInput";
import { useAutoFetchCollection } from "@/hooks/useAutoFetchCollection";
import { isExperiencePublished } from "@/models/Experience";
import { isMerchandiseDraft, MERCHANDISE_API_PATH } from "@/models/Merchandise";
import { getPermissions } from "@/stores/globalStore";

interface MultipleProductSelectorModalProps {
    isOpen: boolean;
    onClose: () => void;
    onConfirm: (values: Product[]) => void;
    kioskProducts: ID[];
}

interface ModalContentProps {
    onClose: () => void;
    onConfirm: (values: Product[]) => void;
    kioskProducts: ID[];
    products: Product[];
}

interface InitialValues {
    searchProductsNotDisplayedValue: string;
    searchProductsDisplayedValue: string;
    products: Product[];
    displayedProducts: Product[];
}

export const MultipleProductSelectorModal = ({
    onClose,
    onConfirm,
    isOpen,
    kioskProducts,
}: MultipleProductSelectorModalProps) => {
    const productObjects = `${ProductObject.EXPERIENCE},${ProductObject.PACKAGE}`;
    const { data: products, isFetching: isFetchingProducts } = useAutoFetchCollection<Product>("products", {
        status: ExperienceStatus.PUBLISHED,
        "object[in]": productObjects,
    });

    const { canViewMerchandise } = getPermissions();
    const { data: merchandises, isFetching: isFetchingMerchandises } = useAutoFetchCollection<Merchandise>(
        MERCHANDISE_API_PATH,
        null,
        { enabled: !!canViewMerchandise },
    );

    const kioskProductOptions = useMemo(() => {
        const kioskMerchandises = canViewMerchandise
            ? (merchandises ?? []).filter(
                  (merchandise) =>
                      !isMerchandiseDraft(merchandise) && merchandise.allowedSources?.includes(MerchandiseSource.KIOSK),
              )
            : [];

        return uniqBy([...(products ?? []), ...kioskMerchandises], (product) => product.id);
    }, [canViewMerchandise, merchandises, products]);

    // Only render ModalContent when products are fetched
    return (
        <Modal size="huge" isOpen={isOpen} onClose={onClose}>
            {!isFetchingProducts && !isFetchingMerchandises && (
                <ModalContent
                    kioskProducts={kioskProducts}
                    products={kioskProductOptions}
                    onClose={onClose}
                    onConfirm={onConfirm}
                />
            )}
        </Modal>
    );
};

const getInitialValue = (
    products: Product[],
    type: "notDisplayed" | "displayed",
    kioskProducts: ID[] = [],
): Product[] => {
    if (type === "displayed") {
        const displayed = [];
        for (const item of kioskProducts) {
            const product = products.find((product: Product) => product.id === item);
            if (product) {
                displayed.push(product);
            }
        }

        return displayed;
    }

    return products.filter((exp: Product) => !kioskProducts.includes(exp.id));
};

const ModalContent = ({ onClose, onConfirm, kioskProducts, products }: ModalContentProps) => {
    const handleSubmit = async (values: InitialValues) => {
        onConfirm(values.displayedProducts);
    };

    const filteredProducts = products.filter(
        (product) => !isExperienceProduct(product) || isExperiencePublished(product),
    );

    return (
        <Formik
            enableReinitialize
            initialValues={{
                searchProductsNotDisplayedValue: "",
                searchProductsDisplayedValue: "",
                products: getInitialValue(filteredProducts, "notDisplayed", kioskProducts),
                displayedProducts: getInitialValue(filteredProducts, "displayed", kioskProducts),
            }}
            onSubmit={handleSubmit}
        >
            {({ handleSubmit, values, setFieldValue, setValues }) => {
                return (
                    <fieldset>
                        <Modal.Header>Update Kiosk Product List</Modal.Header>
                        <Modal.Body className="flex flex-col">
                            <div className="flex justify-between space-x-4">
                                <SearchProducts
                                    name="searchProductsNotDisplayedValue"
                                    label="Products Not Displayed"
                                    value={values.searchProductsNotDisplayedValue}
                                    onChange={(searchResult: string) => {
                                        setFieldValue("searchProductsNotDisplayedValue", searchResult);
                                    }}
                                />
                                <SearchProducts
                                    name="searchProductsDisplayedValue"
                                    label="Products Displayed"
                                    value={values.searchProductsDisplayedValue}
                                    onChange={(searchResult: string) => {
                                        setFieldValue("searchProductsDisplayedValue", searchResult);
                                    }}
                                />
                            </div>
                            <div className="flex h-80 justify-between space-x-4">
                                <DragDropComponent
                                    showPackageBadges
                                    showMerchandiseBadges
                                    data={[...values.products].sort((a, b) =>
                                        a.name.localeCompare(b.name, "en", { sensitivity: "base" }),
                                    )}
                                    displayedProducts={values.displayedProducts}
                                    notDisplayedSearchValue={values.searchProductsNotDisplayedValue}
                                    displayedSearchValue={values.searchProductsDisplayedValue}
                                    onMove={(data: Product[][]) => {
                                        const [products, displayedProducts] = data;
                                        setValues({ ...values, products, displayedProducts });
                                    }}
                                />
                            </div>
                        </Modal.Body>
                        <Modal.Footer className="flex justify-between">
                            <div className="flex space-x-2" />
                            <div className="flex space-x-2">
                                <SecondaryButton onClick={onClose}>Cancel</SecondaryButton>
                                <SubmitButton onClick={handleSubmit}>Save</SubmitButton>
                            </div>
                        </Modal.Footer>
                    </fieldset>
                );
            }}
        </Formik>
    );
};

interface SearchProductsProps {
    onChange: (e: any) => void;
    value: string;
    name: string;
    label: string;
}

const SearchProducts = ({ value, onChange, name, label }: SearchProductsProps) => (
    <div className="flex-1">
        <FormField name={name} label={label}>
            <SearchInput value={value} onChange={onChange} />
        </FormField>
    </div>
);
